import { Button } from '@packages/ui/base/button/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@packages/ui/base/input_group/input_group';
import { cn } from '@packages/ui/styling/cn';
import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import type { ComponentProps } from 'react';

export interface SearchInputProps extends Omit<ComponentProps<'div'>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const SearchInput = ({
  value,
  onChange,
  placeholder = 'Search…',
  className,
  ...props
}: SearchInputProps) => {
  return (
    <InputGroup className={cn('h-10', className)} {...props}>
      <InputGroupAddon align="inline-start">
        <MagnifyingGlassIcon className="size-4" />
      </InputGroupAddon>

      <InputGroupInput
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10"
        aria-label={placeholder}
      />

      {value.length > 0 && (
        <InputGroupAddon align="inline-end">
          <InputGroupButton
            size="icon-xs"
            variant="ghost"
            onClick={() => onChange('')}
            aria-label="Clear search"
          >
            <XIcon className="size-3.5" />
          </InputGroupButton>
        </InputGroupAddon>
      )}
    </InputGroup>
  );
};

export interface SearchEmptyStateProps extends ComponentProps<'div'> {
  onShowAll: () => void;
}

export const SearchEmptyState = ({ onShowAll, className, ...props }: SearchEmptyStateProps) => {
  return (
    <div
      className={cn('flex flex-col items-center justify-center py-10 text-center', className)}
      {...props}
    >
      <div className="bg-primary/10 mb-2 flex size-16 items-center justify-center rounded-full">
        <MagnifyingGlassIcon className="size-7 text-zinc-500" />
      </div>

      <p className="text-muted-foreground mb-4">No matches found</p>

      <Button variant="underline" size="none" onClick={onShowAll} className="text-sm">
        <span>Show all participants</span>
      </Button>
    </div>
  );
};

export interface SearchHiddenCountProps extends ComponentProps<'div'> {
  hiddenCount: number;
  onShowAll: () => void;
}

export const SearchHiddenCount = ({
  hiddenCount,
  onShowAll,
  className,
  ...props
}: SearchHiddenCountProps) => {
  if (hiddenCount <= 0) return null;

  return (
    <div className={cn('text-muted-foreground pt-3 text-center text-sm', className)} {...props}>
      {hiddenCount} {hiddenCount === 1 ? 'participant is' : 'participants are'} hidden.{' '}
      <Button variant="underline" size="none" onClick={onShowAll}>
        <span>Show all</span>
      </Button>
    </div>
  );
};
